/**
 * workspaceRouter.ts
 *
 * The firm side is one shell with three workspaces, and every firm user sees
 * all three (ROLE_VISIBILITY_RECOMMENDATION.md §3.1, decided 2026-10-28):
 *
 *   sell     from lead to signed SOW          opens first for a BD Rep
 *   deliver  from signed SOW to handoff       opens first for everyone else
 *   firm     the tenant itself                opens first for a Billing Admin
 *
 * Role picks the one that opens first and the notifications; it never prunes
 * the tree. A last-used choice per tenant (localStorage) wins over the role
 * default. The client side has one id, `work`: the client home (step 3 of the
 * simplification plan folded the Control Center into it).
 *
 * `tests/unit/workspaces.test.ts` pins these rules.
 */

// ---------------------------------------------------------------------------
// Workspaces
// ---------------------------------------------------------------------------

export type FirmWorkspaceId = 'sell' | 'deliver' | 'firm';
export type ClientWorkspaceId = 'work';
export type WorkspaceId = FirmWorkspaceId | ClientWorkspaceId;

export interface WorkspaceDefinition {
  id: WorkspaceId;
  label: string;
  description: string;
}

export const WORKSPACES: Record<WorkspaceId, WorkspaceDefinition> = {
  sell: { id: 'sell', label: 'Sell', description: 'From lead to signed SOW' },
  deliver: { id: 'deliver', label: 'Deliver', description: 'Your clients and what needs you' },
  firm: { id: 'firm', label: 'Firm', description: 'Portfolio, team, settings and billing' },
  work: { id: 'work', label: 'Work', description: 'Discovery grid and collaboration tools' },
};

/** The firm's three workspaces, in switcher order: the lifecycle first, the firm itself last. */
export const FIRM_WORKSPACES: readonly FirmWorkspaceId[] = ['sell', 'deliver', 'firm'];

export function isFirmWorkspace(id: string): id is FirmWorkspaceId {
  return (FIRM_WORKSPACES as readonly string[]).includes(id);
}

/**
 * Stored preferences and links from before the shell used the old ids:
 * `work` and `command-center` were the delivery side, `pipeline` the sales
 * side, `stories` a marketing view that no longer exists.
 */
const LEGACY_FIRM_IDS: Record<string, FirmWorkspaceId> = {
  work: 'deliver',
  'command-center': 'deliver',
  stories: 'deliver',
  pipeline: 'sell',
};

export function normalizeFirmWorkspace(value: string | null | undefined): FirmWorkspaceId | null {
  if (!value) return null;
  if (isFirmWorkspace(value)) return value;
  return LEGACY_FIRM_IDS[value] ?? null;
}

// ---------------------------------------------------------------------------
// Roles
// ---------------------------------------------------------------------------

const FIRM_DELIVERY_ROLES = ['consultant', 'Engagement Lead', 'engagement_lead', 'Engineer', 'engineer', 'cfAdmin', 'cf-admin', 'consulting_firm_admin'];
const FIRM_ADMIN_ROLES = ['cfAdmin', 'cf-admin', 'consulting_firm_admin'];
const BILLING_ADMIN_ROLES = ['billingAdmin', 'billing_admin', 'billing-admin'];
const BD_REP_ROLES = ['bdRep', 'bd_rep', 'bd-rep'];
const SYSTEM_ADMIN_ROLES = ['systemAdmin', 'system_admin'];

export function getUserRoles(user: any): string[] {
  if (!user) return [];
  const roles: string[] = [];
  if (user.roles && Array.isArray(user.roles)) roles.push(...user.roles);
  if (user.role && !roles.includes(user.role)) roles.push(user.role);
  if (user.activeRole && !roles.includes(user.activeRole)) roles.push(user.activeRole);
  return roles;
}

export function userHasRole(user: any, rolesToCheck: readonly string[]): boolean {
  const userRoles = getUserRoles(user);
  return rolesToCheck.some((r) => userRoles.includes(r));
}

export const isFirmAdminUser = (user: any): boolean => userHasRole(user, FIRM_ADMIN_ROLES);
export const isBillingAdminUser = (user: any): boolean => userHasRole(user, BILLING_ADMIN_ROLES);
export const isBdRepUser = (user: any): boolean => userHasRole(user, BD_REP_ROLES);

// ---------------------------------------------------------------------------
// Tenant Context
// ---------------------------------------------------------------------------

export type TenantContext = 'firm' | 'client' | 'root' | 'unknown';

export function detectTenantContext(): TenantContext {
  if (typeof window === 'undefined') return 'unknown';
  const hostname = window.location.hostname;
  if (
    hostname === 'pathkit.pro' ||
    hostname === 'www.pathkit.pro' ||
    hostname === 'staging.pathkit.pro' ||
    hostname === 'localhost' ||
    hostname.startsWith('localhost:') ||
    hostname.startsWith('127.0.0.1') ||
    hostname.includes('.vercel.app') ||
    hostname.includes('.figma.site')
  ) {
    return 'root';
  }
  // Every tenant subdomain is a consulting firm; a custom domain is treated the same.
  return 'firm';
}

// ---------------------------------------------------------------------------
// localStorage preference
// ---------------------------------------------------------------------------

function getWorkspaceStorageKey(tenant: string | null): string {
  return `pathkit_workspace_${tenant || 'default'}`;
}

export function getLastUsedWorkspace(tenant: string | null): WorkspaceId | null {
  try {
    const stored = localStorage.getItem(getWorkspaceStorageKey(tenant));
    if (!stored) return null;
    if (stored in WORKSPACES) return stored as WorkspaceId;
    return normalizeFirmWorkspace(stored);
  } catch {
    return null;
  }
}

export function setLastUsedWorkspace(tenant: string | null, workspace: WorkspaceId): void {
  try {
    localStorage.setItem(getWorkspaceStorageKey(tenant), workspace);
  } catch {
    // localStorage unavailable
  }
}

// ---------------------------------------------------------------------------
// Accessible workspaces: what the user can open
// ---------------------------------------------------------------------------

export function getAccessibleWorkspaces(user: any, tenantContext: TenantContext, _engagements?: any[]): WorkspaceId[] {
  // The client side is one screen, the home, for every client role.
  if (tenantContext === 'client') return ['work'];
  // Firm side: everyone sees all three; nothing is hidden by title.
  return [...FIRM_WORKSPACES];
}

// ---------------------------------------------------------------------------
// Default workspace: where a role lands
// ---------------------------------------------------------------------------

export function resolveDefaultWorkspace(user: any, tenantContext: TenantContext, _engagements?: any[], _tenant?: string | null): WorkspaceId {
  if (tenantContext === 'client') return 'work';
  if (!user) return 'deliver';
  const delivers = userHasRole(user, FIRM_DELIVERY_ROLES) || userHasRole(user, SYSTEM_ADMIN_ROLES);
  // A person whose only firm role is Billing Admin lands on the firm itself (Firm › Billing).
  if (isBillingAdminUser(user) && !delivers && !isBdRepUser(user)) return 'firm';
  // A BD Rep with no delivery role lands on Sell; one who also delivers lands on Deliver, the paid work.
  if (isBdRepUser(user) && !delivers) return 'sell';
  return 'deliver';
}

// ---------------------------------------------------------------------------
// Full resolution
// ---------------------------------------------------------------------------

export interface WorkspaceRouterResult {
  defaultWorkspace: WorkspaceId;
  accessibleWorkspaces: WorkspaceId[];
  tenantContext: TenantContext;
  showSwitcher: boolean;
}

export function resolveWorkspaceRouter(user: any, engagements?: any[], tenant?: string | null, contextOverride?: TenantContext): WorkspaceRouterResult {
  const tenantContext = contextOverride || detectTenantContext();
  const accessible = getAccessibleWorkspaces(user, tenantContext, engagements);
  const lastUsed = getLastUsedWorkspace(tenant ?? null);
  const defaultWorkspace = lastUsed && accessible.includes(lastUsed)
    ? lastUsed
    : resolveDefaultWorkspace(user, tenantContext, engagements, tenant);
  return { defaultWorkspace, accessibleWorkspaces: accessible, tenantContext, showSwitcher: accessible.length >= 2 };
}
